import { useId, useState } from 'react';
import { buildExport, EXPORT_PRESETS, type ExportDataset, useExportCatalog } from '../api/exports';
import { monthLabel, recentMonths } from '../api/payroll';
import { Icon } from '../components/Icon';
import { EmptyState, ErrorState, LoadingState } from '../components/states';
import { usePageTitle } from '../layout/shellContext';

/** Выгрузки: администратор, HR и руководители собирают Excel или CSV по своим сотрудникам. */
export function ExportsPage() {
  usePageTitle('Выгрузки');
  const catalog = useExportCatalog();
  const formatId = useId();
  const [chosen, setChosen] = useState<string[]>(['attendance-summary', 'attendance-days']);
  const [month, setMonth] = useState<string | null>(null);
  const [format, setFormat] = useState<'xlsx' | 'csv'>('xlsx');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);

  if (catalog.isPending) return <LoadingState label="Загружаю наборы данных" />;
  if (catalog.isError) return <ErrorState error={catalog.error} onRetry={() => void catalog.refetch()} />;
  const data = catalog.data;
  const months = recentMonths(month ?? data.month, 6);
  const current = month ?? data.month;
  const available = data.datasets.filter((dataset) => dataset.available);
  const selected = chosen.filter((key) => available.some((dataset) => dataset.key === key));
  const csvBlocked = format === 'csv' && selected.length > 1;

  const toggle = (dataset: ExportDataset) => {
    setDone(null);
    setError(null);
    setChosen((previous) =>
      previous.includes(dataset.key)
        ? previous.filter((key) => key !== dataset.key)
        : [...previous, dataset.key],
    );
  };

  const run = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const size = await buildExport({ datasets: selected, month: current, format });
      setDone(`Файл собран: ${Math.max(1, Math.round(size / 1024))} КБ`);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  if (!available.length) {
    return (
      <div className="page">
        <section className="panel">
          <EmptyState title="Выгрузки вам не доступны">
            Данные для выгрузки видят руководители по своим сотрудникам, HR в своей области и администратор.
          </EmptyState>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <section className="panel">
        <div className="panel-header">
          <h2>Готовые сборки</h2>
        </div>
        <p className="muted">
          Отметьте наборы данных, выберите месяц и соберите файл. В выгрузку попадают только те сотрудники,
          которых вам видно в системе; каждая выгрузка отмечается в журнале действий.
        </p>
        <div className="pill-row">
          {EXPORT_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              className="pill-toggle"
              onClick={() => {
                setChosen([...preset.datasets]);
                setDone(null);
              }}
            >
              {preset.title}
            </button>
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2>Наборы данных</h2>
          <span className="muted">{selected.length} выбрано</span>
        </div>
        <ul className="dataset-list">
          {data.datasets.map((dataset) => (
            <li key={dataset.key} className={dataset.available ? undefined : 'dataset-off'}>
              <label className="dataset-row">
                <input
                  type="checkbox"
                  checked={chosen.includes(dataset.key)}
                  disabled={!dataset.available}
                  onChange={() => toggle(dataset)}
                />
                <span>
                  <b>{dataset.title}</b>
                  <small className="dataset-note">{dataset.description}</small>
                  <small className="dataset-note muted">
                    {dataset.available ? `сотрудников: ${dataset.people}` : 'вам не доступен'}
                  </small>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2>Период и формат</h2>
        </div>
        <fieldset className="pill-row period-pills">
          <legend className="visually-hidden">Месяц</legend>
          {months.map((item) => (
            <button
              key={item}
              type="button"
              className="pill-toggle"
              aria-pressed={item === current}
              onClick={() => {
                setMonth(item);
                setDone(null);
              }}
            >
              {monthLabel(item)}
            </button>
          ))}
        </fieldset>
        <div className="payroll-page-tools">
          <label className="field" htmlFor={formatId}>
            <span>Формат</span>
            <select
              id={formatId}
              className="select"
              value={format}
              onChange={(event) => setFormat(event.target.value as 'xlsx' | 'csv')}
            >
              <option value="xlsx">Excel (.xlsx), лист на набор</option>
              <option value="csv">CSV, один набор</option>
            </select>
          </label>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !selected.length || csvBlocked}
            onClick={() => void run()}
          >
            <Icon name="download" size={14} />
            {busy ? 'Собираю…' : 'Собрать файл'}
          </button>
        </div>
        {csvBlocked ? (
          <p className="notice">
            <Icon name="alert" size={14} />
            CSV собирается из одного набора. Выберите один набор или формат Excel.
          </p>
        ) : null}
        {error ? <ErrorState title="Файл не собрался" error={error} /> : null}
        {done ? (
          <p className="notice notice-ok">
            <Icon name="check" size={14} />
            {done}
          </p>
        ) : null}
      </section>
    </div>
  );
}
